/**
 * مصادقة Supabase: الجلسات والتحقق من البريد واستعادة كلمة المرور.
 * الصلاحيات لا تأتي من هنا؛ تأتي من memberships/roles عبر RLS.
 */

import type { AuthGateway, AuthSession, SignInResult, SignUpInput } from '../types';
import { AppError } from '@/domain/errors';
import { getSupabaseClient } from './client';

function redirectUrl(): string | undefined {
  const configured = import.meta.env.VITE_AUTH_REDIRECT_URL;
  if (configured) return configured;
  if (typeof window !== 'undefined') return `${window.location.origin}/auth/callback`;
  return undefined;
}

export class SupabaseAuthGateway implements AuthGateway {
  async getSession(): Promise<AuthSession | null> {
    const { data, error } = await getSupabaseClient().auth.getSession();
    if (error) throw new AppError('network', { userMessage: 'تعذّر التحقق من الجلسة.', cause: error });
    const session = data.session;
    if (!session?.user) return null;
    return {
      userId: session.user.id,
      email: session.user.email ?? '',
      expiresAt: new Date((session.expires_at ?? 0) * 1000).toISOString(),
    };
  }

  async signIn(email: string, password: string): Promise<SignInResult> {
    const { data, error } = await getSupabaseClient().auth.signInWithPassword({
      email: email.trim().toLowerCase(),
      password,
    });
    if (error) {
      throw AppError.unauthenticated(
        error.message.toLowerCase().includes('email not confirmed')
          ? 'يجب تأكيد البريد الإلكتروني قبل الدخول.'
          : 'بيانات الدخول غير صحيحة.',
      );
    }
    return {
      userId: data.user.id,
      email: data.user.email ?? email,
      requiresEmailConfirmation: !data.user.email_confirmed_at,
    };
  }

  async signUp(input: SignUpInput): Promise<SignInResult> {
    const { data, error } = await getSupabaseClient().auth.signUp({
      email: input.email.trim().toLowerCase(),
      password: input.password,
      options: { data: { full_name: input.fullName.trim() }, emailRedirectTo: redirectUrl() },
    });
    if (error) throw AppError.validation(error.message);
    if (!data.user) throw AppError.validation('تعذّر إنشاء الحساب.');
    return {
      userId: data.user.id,
      email: data.user.email ?? input.email,
      requiresEmailConfirmation: !data.session,
    };
  }

  async signOut(): Promise<void> {
    const { error } = await getSupabaseClient().auth.signOut();
    if (error) throw new AppError('network', { userMessage: 'تعذّر تسجيل الخروج. حاول مجددًا.', cause: error });
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    const client = getSupabaseClient();
    const { data: userData, error: userError } = await client.auth.getUser();
    if (userError || !userData.user?.email) {
      throw AppError.unauthenticated('الجلسة منتهية. سجّل الدخول مجددًا.');
    }
    // إعادة التحقق من الكلمة الحالية قبل التغيير.
    const { error: reauthError } = await client.auth.signInWithPassword({
      email: userData.user.email,
      password: currentPassword,
    });
    if (reauthError) throw AppError.unauthenticated('كلمة المرور الحالية غير صحيحة.');
    const { error } = await client.auth.updateUser({ password: newPassword });
    if (error) throw AppError.validation(error.message);
  }

  async requestPasswordReset(email: string): Promise<void> {
    const { error } = await getSupabaseClient().auth.resetPasswordForEmail(email.trim().toLowerCase(), {
      redirectTo: redirectUrl(),
    });
    // لا نكشف إن كان البريد مسجّلًا أم لا.
    if (error && error.status !== 404) {
      throw new AppError('network', { userMessage: 'تعذّر إرسال رابط الاستعادة. حاول لاحقًا.', cause: error });
    }
  }  async requestPasswordResetCode(email: string): Promise<void> {
    // القالب الافتراضي يرسل رابطًا. لإرسال كود تحقق (6–10 أرقام حسب إعداد
    // Email OTP Length) يجب أن يحتوي قالب «Reset password» على {{ .Token }}.
    const { error } = await getSupabaseClient().auth.resetPasswordForEmail(email.trim().toLowerCase(), {
      redirectTo: redirectUrl(),
    });
    if (error && error.status !== 404) {
      throw new AppError('network', { userMessage: 'تعذّر إرسال كود الاستعادة. حاول لاحقًا.', cause: error });
    }
  }

  async verifyPasswordResetCode(email: string, code: string): Promise<void> {
    const { error } = await getSupabaseClient().auth.verifyOtp({
      email: email.trim().toLowerCase(),
      token: code.replace(/\s+/g, ''),
      type: 'recovery',
    });
    if (error) {
      throw AppError.validation('الكود غير صحيح أو انتهت صلاحيته. اطلب كودًا جديدًا.');
    }
  }

  async completePasswordReset(newPassword: string): Promise<void> {
    const { error } = await getSupabaseClient().auth.updateUser({ password: newPassword });
    if (error) throw AppError.validation(error.message);
  }

  async requestEmailChange(newEmail: string): Promise<void> {
    const { error } = await getSupabaseClient().auth.updateUser(
      { email: newEmail.trim().toLowerCase() },
      { emailRedirectTo: redirectUrl() },
    );
    if (error) throw AppError.validation(error.message);
  }

  onAuthStateChange(callback: (session: AuthSession | null) => void): () => void {
    const { data } = getSupabaseClient().auth.onAuthStateChange((_event, session) => {
      if (!session?.user) {
        callback(null);
        return;
      }
      callback({
        userId: session.user.id,
        email: session.user.email ?? '',
        expiresAt: new Date((session.expires_at ?? 0) * 1000).toISOString(),
      });
    });
    return () => data.subscription.unsubscribe();
  }
}
